import React, { useState, useEffect } from "react";
import { Card, CardContent, Avatar, Box, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ActivityReviewModal from "./ActivityReviewModal";
import { getActivityMessages } from "services/activityService";
import CircularProgress from "@mui/material/CircularProgress";
import AvatarWithLoader from "../../../Components/Avatar/AvatarWithLoader";

// Format the date to something like: "Tuesday, July 1"
function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function ActivityChat({ activity, userId }) {
  const [messages, setMessages] = useState([]);
  const [openActivityReviewModal, setOpenActivityReviewModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleCloseActivityReviewModal = () => {
    setOpenActivityReviewModal(false);
  };

  useEffect(() => {
    setLoading(true);
    getActivityMessages(userId, activity._id)
      .then((data) => {
        if (data.success) {
          setMessages(data.data);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching messages:", error);
        setLoading(false);
      });
  }, [userId, activity._id]);

  // Group messages by date for date separators
  let lastDate = null;

  return (
    <Card
      sx={{
        flex: 1,
        height: 600,
        boxShadow: 2,
        borderRadius: 3,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent sx={{ p: 2, pb: 0, borderBottom: "1px solid #eee" }}>
        <Box display="flex" alignItems="center" gap={2} p={2}>
          <Avatar
            src={activity.image}
            variant="rounded"
            sx={{ width: 48, height: 48 }}
          />
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography
              variant="h5"
              fontWeight={600}
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setOpenActivityReviewModal(true);
              }}
            >
              {activity.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {activity.participantCount} Members
            </Typography>
          </Box>
        </Box>
      </CardContent>
      <Box sx={{ flex: 1, overflowY: "auto", p: 2, background: "#f8f9fa" }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="400px"
          >
            <CircularProgress size={40} />
          </Box>
        ) : messages.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="400px"
          >
            <Typography variant="body2" color="textSecondary">
              No messages found
            </Typography>
          </Box>
        ) : (
          messages.map((msg) => {
            const showDate = msg.date !== lastDate;
            lastDate = msg.date;
            return (
              <React.Fragment key={msg._id}>
                {showDate && (
                  <Box textAlign="center" my={2}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ color: "#444" }}
                    >
                      {formatDate(msg.date)}
                    </Typography>
                  </Box>
                )}
                <Box
                  display="flex"
                  flexDirection={msg.sender.isMe ? "row-reverse" : "row"}
                  alignItems="flex-start"
                  mb={2}
                  gap={1.5}
                >
                  <Box>
                    <AvatarWithLoader
                      src={`/${msg.sender.avatar}`}
                      alt={msg.sender.name}
                    />
                  </Box>

                  <Box maxWidth="75%">
                    <Box
                      sx={{
                        background: msg.sender.isMe ? "#dee2e6" : "#ffffff",
                        color: "#8392AB",
                        borderRadius: 3,
                        px: 2,
                        py: 1.2,
                        boxShadow: "0 0 40px rgba(0, 0, 0, 0.05)",
                        mb: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: 14 }}>
                        {msg.message}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#888",
                          display: "block",
                          textAlign: msg.sender.isMe ? "right" : "left",
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        {msg.time}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </React.Fragment>
            );
          })
        )}
      </Box>
      {openActivityReviewModal && (
        <ActivityReviewModal
          activityId={activity._id}
          open={openActivityReviewModal}
          handleClose={handleCloseActivityReviewModal}
        />
      )}
    </Card>
  );
}

export default ActivityChat;
